import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { EmergencyCallsState } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type { SinatraState } from '@rsos/sinatra';
import { transcriptionApi } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import type { TranscriptionsState } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionSlice.types';

const CALL_DATA_EVENT_TYPE = 'call_data.event';
const CALL_ENDED_EVENT_TYPE = 'call_ended';

interface CallEndedWebSocketMessage {
  topic: string;
  type: typeof CALL_DATA_EVENT_TYPE;
  body: {
    event: {
      event_name: 'call_status';
      event_type: typeof CALL_ENDED_EVENT_TYPE;
    };
    metadata: {
      application: string;
      created_time: number;
      call_id: number;
    };
  };
}

type TranscriptionThunkDispatch = ThunkDispatch<
  {
    transcriptions: TranscriptionsState;
    emergencyCalls: EmergencyCallsState;
    sinatra: SinatraState;
  },
  string,
  UnknownAction
>;

/**
 * Custom hook that handles call ended events from the StreamWS websocket.
 * When a call_data.event with event_type: "call_ended" is received,
 * it triggers a fetch for the final transcription data including audio.
 *
 * This provides a more reliable way to detect call endings compared to
 * relying on the is_final flag in transcription summaries.
 */
const useHandleCallEndedEvents = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch: TranscriptionThunkDispatch = useDispatch();

  const handleCallEndedEvent = useCallback(
    (msg: any) => {
      if (
        msg?.type !== CALL_DATA_EVENT_TYPE ||
        msg?.body?.event?.event_type !== CALL_ENDED_EVENT_TYPE ||
        msg?.body?.event?.event_name !== 'call_status' ||
        !msg?.body?.metadata?.call_id ||
        !msg?.topic
      ) {
        return;
      }

      const message = msg as CallEndedWebSocketMessage;

      const parts = `${message.topic}`.split('tel://');

      const callerId = parts[1];

      dispatch(
        transcriptionApi.endpoints.fetchTranscriptions.initiate(callerId, {
          forceRefetch: true,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleCallEndedEvent: handleCallEndedEvent,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleCallEndedEvent');
      };
    }
  }, [StreamWS, handleCallEndedEvent]);

  return null;
};

export default useHandleCallEndedEvents;
